import {
  DeesElement,
  css,
  cssManager,
  customElement,
  html,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { demoFunc } from './dees-hint.demo.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-hint': DeesHint;
  }
}

export type THintType = 'info' | 'success' | 'warning' | 'error';

const hintIcons: Record<THintType, string> = {
  info: 'lucide:Info',
  success: 'lucide:CircleCheck',
  warning: 'lucide:TriangleAlert',
  error: 'lucide:CircleAlert',
};

/**
 * An inline advisory note next to the thing it explains. Use `text` for a plain
 * sentence and the default slot for markup; both render when both are set.
 * For a hover-revealed label on a control, use the control's own tooltip.
 */
@customElement('dees-hint')
export class DeesHint extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Feedback'];

  @property({ type: String, reflect: true })
  accessor type: THintType = 'info';

  /** The hint sentence. Rendered before any slotted content. */
  @property({ type: String })
  accessor text: string = '';

  @state()
  private accessor hasSlottedContent = false;

  constructor() {
    super();
    domtools.elementBasic.setup();
  }

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-primary);
        --dees-hint-accent: var(--dees-color-accent-primary);
      }

      :host([type='success']) { --dees-hint-accent: var(--dees-color-text-success); }
      :host([type='warning']) { --dees-hint-accent: var(--dees-color-text-warning); }
      :host([type='error']) { --dees-hint-accent: var(--dees-color-text-error); }

      .mainbox {
        box-sizing: border-box;
        display: flex;
        align-items: flex-start;
        gap: var(--dees-spacing-sm);
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border: 1px solid color-mix(in srgb, var(--dees-hint-accent) 28%, transparent);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: color-mix(in srgb, var(--dees-hint-accent) 8%, transparent);
        font-size: var(--dees-font-footnote-size);
        line-height: var(--dees-font-footnote-line);
        overflow-wrap: anywhere;
      }

      /* An element with neither text nor slotted content reserves no space. */
      .mainbox.empty {
        display: none;
      }

      .icon {
        flex-shrink: 0;
        color: var(--dees-hint-accent);
        font-size: 16px;
        /* Optically aligns the glyph with the first line of text. */
        margin-top: 1px;
      }

      .content {
        min-width: 0;
        color: var(--dees-color-text-secondary);
      }
    `];

  public render(): TemplateResult {
    const isEmpty = !this.text && !this.hasSlottedContent;
    return html`
      <div class="mainbox ${isEmpty ? 'empty' : ''}" role="note">
        <dees-icon class="icon" aria-hidden="true" .icon=${hintIcons[this.type] ?? hintIcons.info}></dees-icon>
        <div class="content">${this.text}<slot @slotchange=${this.handleSlotChange}></slot></div>
      </div>
    `;
  }

  private handleSlotChange = (eventArg: Event) => {
    const assigned = (eventArg.target as HTMLSlotElement).assignedNodes({ flatten: true });
    this.hasSlottedContent = assigned.some((node) =>
      node.nodeType === Node.ELEMENT_NODE || (node.textContent ?? '').trim().length > 0);
  };
}
